/**
 * GameDataManager.js
 * Handles the saved game data
 */

import { CoreEvents, Game, SaveManager, SavePrefixType } from "pixi_core";
import { Cinematic, DEFAULT_HERO_TEAM, FIRST_LEVEL, GameDataIds, Tutorials, Heroes, LAST_LEVEL, GameEvents } from "../Constants";

export class GameDataManager extends SaveManager {
    constructor() {
        super();

        /** @private @type {Boolean} */
        this._initialized = false;

        /** @private @type {{progression: number, heroTeam: Heroes[]}} */
        this._saveData = null;

        /** @private @type {{introPlayed: Boolean, postTutoPlayed: Boolean, endingPlayed: Boolean}} */
        this._cinematic = null;

        /** @private @type {import("../Constants").TutorialSaveData} */
        this._tutorialSteps = null;

        if (this.isReady()) {
            this._onSaveDataReady();
        } else {
            Game.once("SaveManager:Ready", this._onSaveDataReady, this);
        }

        /** @private @type {Number} */
        this._currentLevel = 1;

        Game.on(GameEvents.eLEVEL_MANAGER_GAME_PROGRESS, this._onGameProgress, this);
        Game.on(GameEvents.eCINEMATIC_PLAYED, this._onCinematicPlayed, this);
        Game.on(GameEvents.eTUTORIAL_STEP_PLAYED, this._onTutorialStepPlayed, this);
        Game.on(GameEvents.eLEVEL_SELECTION_SET_LEVEL, this._onLevelSelected, this);
        Game.on(GameEvents.eAVENGERS_ASSEMBLE, this._setHeroTeam, this);
        Game.on(GameEvents.eLEVEL_SELECTION_UNLOCK_ALL_LEVELS_CONFIRMED, this._unlockAllLevels, this);
    }

    /**
     * @public
     * @returns {Boolean}
     */
    isInitialized() {
        return this._initialized;
    }

    /**
     * Callback when the save manager is ready to be used
     */
    _onSaveDataReady() {
        this.getObject(GameDataIds.eGAME, SavePrefixType.eGAME)
            .then((data) => {
                this._saveData = data;

                console.log("[GameDataManager] saved data : " + JSON.stringify(this._saveData));
                Game.fire(GameEvents.eGAMEDATAMANAGER_SAVEDATALOADED);
                this._checkCompletion();
            })
            .catch((error) => {
                console.log("[GameDataManager._onSaveDataReady] No save data found : " + String(error));
                this._createDefaultSave();

                console.log("[GameDataManager] saved data : " + JSON.stringify(this._saveData));
                Game.fire(GameEvents.eGAMEDATAMANAGER_SAVEDATALOADED);
                this._checkCompletion();
            });

        this.getObject(GameDataIds.eCINEMATIC, SavePrefixType.eGAME)
            .then((data) => {
                this._cinematic = data;

                console.log("[GameDataManager] cinematic data : " + JSON.stringify(this._cinematic));
                this._checkCompletion();
            })
            .catch((error) => {
                this._resetCinematic();

                console.log("[GameDataManager] cinematic data : " + JSON.stringify(this._cinematic));
                this._checkCompletion();
            });

        this.getObject(GameDataIds.eTUTORIAL, SavePrefixType.eGAME)
            .then((data) => {
                this._tutorialSteps = data;

                console.log("[GameDataManager] tutorial data : " + JSON.stringify(this._tutorialSteps));
                this._checkCompletion();
            })
            .catch((error) => {
                this._resetTutorials();

                console.log("[GameDataManager] tutorial data : " + JSON.stringify(this._tutorialSteps));
                this._checkCompletion();
            });
    }

    /**
     * Verifies the loading completion
     * @private
     */
    _checkCompletion() {
        if (!this._initialized && this._saveData && this._cinematic && this._tutorialSteps) {
            this._initialized = true;
            Game.fire(GameEvents.eGAMEDATAMANAGER_READY);
        }
    }

    /**
     * @private
     * Creates the save data with default starting values
     */
    _createDefaultSave() {
        let level = FIRST_LEVEL;
        let team = [];
        for (let i = 0; i < DEFAULT_HERO_TEAM.length; ++i) {
            team.push(DEFAULT_HERO_TEAM[i]);
        }
        this._saveData = { progression: level, heroTeam: team };
    }

    /**
     * @private
     * Resets the cinematic data with defaults
     */
    _resetCinematic() {
        this._cinematic = { introPlayed: false, postTutoPlayed: false, endingPlayed: false };
    }

    /**
     * @private
     * Resets the tutorial data with defaults
     */
    _resetTutorials() {
        this._tutorialSteps = {
            Level1: false,
            Level2: false,
            AllOutAttack: false,
            HeroSelection: false,
            BubbleHeal: false,
            BubbleHidden: false,
            BubbleChameleon: false,
            BubbleStone: false,
            BubbleColorless: false,
            BubbleBlooming: false,
            BubbleBloomingStone: false
        };
    }

    /**
     * Callback to save the progression when a level is completed
     * @private
     * @param {Number} level
     */
    _onGameProgress(level) {
        if (level > LAST_LEVEL) {
            level = LAST_LEVEL;
        }

        this._saveData.progression = level;

        this.setObject(GameDataIds.eGAME, this._saveData, SavePrefixType.eGAME).catch((error) => {
            console.warn("[GameDataManager._onGameProgress] Error saving save game data: ", error);
        });
    }

    /**
     * Clear all saved data of the given type
     * @public
     * @param {String} type
     */
    clearSaveData(type) {
        super.clearSaveData(type);
        if (type === SavePrefixType.eGAME) {
            this._createDefaultSave();
            this._resetCinematic();
            this._resetTutorials();

            Game.fire(CoreEvents.eGAME_DATA_CLEARED);
        }
    }

    /**
     * Get the progression
     * @public
     * @return {Number}
     */
    getProgression() {
        return this._saveData.progression;
    }

    /**
     * Get the current Hero Team
     * @public
     * @return {Heroes[]}
     */
    getHeroTeam() {
        return this._saveData.heroTeam;
    }

    /**
     * Set the current team of heroes
     * @public
     * @param {Heroes[]} heroes
     */
    _setHeroTeam(heroes) {
        this._saveData.heroTeam = heroes;

        this.setObject(GameDataIds.eGAME, this._saveData, SavePrefixType.eGAME).catch((error) => {
            console.warn("[GameDataManager._onGameProgress] Error saving save game data: ", error);
        });
    }

    /**
     * Return if there's a non-empty save data or not
     * @public
     * @returns {Boolean}
     */
    haveSaveData() {
        let haveSaveData = false;

        haveSaveData =
            this._saveData.progression !== FIRST_LEVEL ||
            this._saveData.heroTeam.toString() !== DEFAULT_HERO_TEAM.toString() ||
            this.hasCinematicPlayed(Cinematic.eANY);

        return haveSaveData;
    }

    /**
     * Has the cinematic been played?
     * @public
     * @param {Cinematic} cinematic
     * @return {Boolean}
     */
    hasCinematicPlayed(cinematic) {
        if (cinematic !== Cinematic.eANY) {
            return this._cinematic && this._cinematic[cinematic] === true;
        } else {
            return (
                this._cinematic &&
                (this._cinematic.introPlayed === true || this._cinematic.postTutoPlayed === true || this._cinematic.endingPlayed === true)
            );
        }
    }

    /**
     * @public
     * @param {Tutorials} tutorialIndex
     */
    hasPlayedTutorial(tutorialIndex) {
        if (tutorialIndex !== Tutorials.eANY) {
            return this._tutorialSteps && this._tutorialSteps[tutorialIndex] === true;
        }
    }

    /**
     * Callback when the cinematic finish playing
     * @param {Cinematic} cinematic
     * @private
     */
    _onCinematicPlayed(cinematic) {
        if (this._cinematic) {
            this._cinematic[cinematic] = true;
            this.setObject(GameDataIds.eCINEMATIC, this._cinematic, SavePrefixType.eGAME).catch((error) => {
                console.warn("[GameDataManager._onCinematicPlayed] Error saving cinematic data: ", error);
            });
        }
    }

    /**
     * Callback when a tutorial step is shown.
     * @param {Tutorials} tutorialStep
     * @private
     */
    _onTutorialStepPlayed(tutorialStep) {
        if (this._tutorialSteps) {
            this._tutorialSteps[tutorialStep] = true;
            this.setObject(GameDataIds.eTUTORIAL, this._tutorialSteps, SavePrefixType.eGAME).catch((error) => {
                console.warn("[GameDataManager._onTutorialStepPlayed] Error saving tutorial data: ", error);
            });
        }
    }

    /**
     * @private
     * @param {Number} levelIndex
     */
    _onLevelSelected(levelIndex) {
        this._currentLevel = Game.utils.assertNumber(levelIndex, 1);
    }

    /**
     * @private
     */
    _unlockAllLevels() {
        this._saveData.progression = LAST_LEVEL;
        this.setObject(GameDataIds.eGAME, this._saveData, SavePrefixType.eGAME);
    }
}
